import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import type { SubCategoryFormValue } from '../types/categories.types';

interface CategoryHierarchyBlockProps {
  items: SubCategoryFormValue[];
  onAddSub: () => void;
  onRemoveSub: (id: string) => void;
  onUpdateSub: (id: string, patch: Partial<SubCategoryFormValue>) => void;
  onAddChild: (parentId: string) => void;
  onRemoveChild: (parentId: string, id: string) => void;
  onUpdateChild: (parentId: string, id: string, patch: Partial<SubCategoryFormValue>) => void;
  isDisabled?: boolean;
}

export function CategoryHierarchyBlock({
  items,
  onAddSub,
  onRemoveSub,
  onUpdateSub,
  onAddChild,
  onRemoveChild,
  onUpdateChild,
  isDisabled,
}: CategoryHierarchyBlockProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section className="space-y-2 rounded-md border border-slate-100 bg-white p-3 shadow-sm flex-1 min-h-0 flex flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-slate-50 pb-2">
        <h2 className="text-xs text-slate-900">Cấu trúc phân cấp</h2>
        <button
          type="button"
          onClick={() => {
            onAddSub();
          }}
          className="flex h-9 items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 text-xs text-emerald-700 hover:bg-emerald-100 transition-colors"
          disabled={isDisabled}
        >
          <Plus className="h-4 w-4" />
          <span>Thêm mục con</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto pr-1 space-y-3 custom-scrollbar min-h-0 pb-10">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-slate-400 border-2 border-dashed border-slate-50 rounded-md">
            <i className="ri-node-tree text-3xl mb-2" />
            <p className="text-xs text-slate-400">Chưa có cấu trúc phân cấp nào.</p>
          </div>
        ) : (
          items.map((sub, index) => {
            const isExpanded = expandedIds.has(sub.id);
            return (
              <div key={sub.id} className={`space-y-3 rounded-md border transition-all duration-200 ${isExpanded ? 'border-emerald-100 bg-emerald-50/5 p-3' : 'border-slate-100 bg-slate-50/20 p-2'}`}>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => toggleExpand(sub.id)}
                    className="flex flex-1 items-center gap-2 text-left hover:opacity-80 transition-opacity min-w-0"
                    disabled={isDisabled}
                  >
                    <div className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${isExpanded ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </div>
                    <span className="text-xs tracking-wide text-slate-700 font-medium truncate">
                      Mục con {index + 1}: {sub.name || '---'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveSub(sub.id)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all"
                    disabled={isDisabled}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {isExpanded && (
                  <div className="space-y-4 pt-2 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="block space-y-1">
                        <span className="text-xs text-slate-500 font-medium">Tên mục con</span>
                        <input
                          value={sub.name}
                          onChange={(event) => onUpdateSub(sub.id, { name: event.target.value })}
                          className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm focus:border-emerald-500 focus:outline-none bg-white shadow-sm"
                          placeholder="Nhập tên mục con"
                          disabled={isDisabled}
                        />
                      </label>
                      <label className="block space-y-1">
                        <span className="text-xs text-slate-500 font-medium">Mã mục con</span>
                        <input
                          value={sub.code}
                          onChange={(event) => onUpdateSub(sub.id, { code: event.target.value })}
                          className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm focus:border-emerald-500 focus:outline-none bg-white shadow-sm"
                          placeholder="Mã tự động"
                          disabled={isDisabled}
                        />
                      </label>
                    </div>

                    <div className="space-y-2 rounded-md border border-slate-100 bg-white p-3 shadow-sm">
                      <div className="flex items-center justify-between gap-2 border-b border-slate-50 pb-2 mb-1">
                        <p className="text-xs text-slate-900 font-medium">Mục cháu (Cấp 3)</p>
                        <button
                          type="button"
                          onClick={() => onAddChild(sub.id)}
                          className="flex h-7 items-center gap-1 rounded-md border border-emerald-100 bg-emerald-50 px-2 text-xs text-emerald-700 hover:bg-emerald-100 transition-all"
                          disabled={isDisabled}
                        >
                          <Plus className="h-3 w-3" />
                          <span>Thêm mục cháu</span>
                        </button>
                      </div>

                      {(sub.subSubCategories || []).length === 0 ? (
                        <div className="py-4 text-center">
                          <p className="text-xs text-slate-400 italic">Chưa có mục cháu nào.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {(sub.subSubCategories || []).map((child, childIndex) => (
                            <div key={child.id} className="group relative space-y-2 rounded-md border border-slate-50 bg-slate-50/30 p-2.5 hover:border-emerald-100 transition-colors">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <p className="text-xs text-slate-500 font-medium">Mục cháu {childIndex + 1}</p>
                                <button
                                  type="button"
                                  onClick={() => onRemoveChild(sub.id, child.id)}
                                  className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                                  disabled={isDisabled}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <input
                                  value={child.name}
                                  onChange={(event) => onUpdateChild(sub.id, child.id, { name: event.target.value })}
                                  className="h-9 w-full rounded-md border border-slate-100 px-3 text-xs focus:border-emerald-500 focus:outline-none bg-white shadow-sm"
                                  placeholder="Tên mục cháu..."
                                  disabled={isDisabled}
                                />
                                <input
                                  value={child.code}
                                  onChange={(event) => onUpdateChild(sub.id, child.id, { code: event.target.value })}
                                  className="h-9 w-full rounded-md border border-slate-100 px-3 text-xs focus:border-emerald-500 focus:outline-none bg-white shadow-sm"
                                  placeholder="Mã mục cháu..."
                                  disabled={isDisabled}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
